import { useState, useEffect, useRef } from 'react';

import { api } from '../lib/api';
import { Check, Copy, CheckCircle2, ArrowLeft, Cpu } from 'lucide-react';

type Step = 'pricing' | 'paying' | 'success' | 'error';

interface PlanItem {
  code: string; name: string; kind: string; product: string;
  durationDays: number; displayPrice: string; description: string;
  features: string[]; sortOrder: number;
}

const PLAN_FEATURES = {
  annual: ['实时模型目录同步 API', '智能体自动发现新模型', '可用性实时探测数据', '厂商新闻动态分析', '可取消，随时续费'],
  lifetime: ['年度版所有功能', '永久使用，无需续费', '优先支持新功能', '同一个同步令牌', '所有设备通用'],
};

/** 定价方案核心区块（支付 + 兑换码激活 + 客服二维码），供页面设计器作为系统区块渲染 */
export function PricingBlock({ title, subtitle, csQr, csText }: { title?: string; subtitle?: string; csQr?: string; csText?: string }) {
  const [step, setStep] = useState<Step>('pricing');
  const [selectedPlan, setSelectedPlan] = useState<'annual' | 'lifetime'>('annual');
  const [email, setEmail] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [outTradeNo, setOutTradeNo] = useState('');
  const [syncToken, setSyncToken] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [payEnabled, setPayEnabled] = useState(true);
  const [plans, setPlans] = useState<PlanItem[]>([]);
  const [selectedPlanItem, setSelectedPlanItem] = useState<PlanItem | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [redeemOpen, setRedeemOpen] = useState(false);
  const [cardCode, setCardCode] = useState('');
  const [redeemEmail, setRedeemEmail] = useState('');
  const [redeemMsg, setRedeemMsg] = useState('');
  const [redeemOk, setRedeemOk] = useState(false);
  const [redeemToken, setRedeemToken] = useState('');

  useEffect(() => {
    fetch(api('/api/payment/enabled')).then(r => r.json()).then(d => setPayEnabled(d.enabled)).catch(() => {});
    fetch(api('/api/payment/plans')).then(r => r.json()).then(d => setPlans(d.plans || [])).catch(() => {});
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleRedeem = async () => {
    setRedeemMsg('');
    setRedeemOk(false);
    if (!cardCode.trim() || !redeemEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(redeemEmail)) {
      setRedeemMsg('请输入兑换码与有效的邮箱地址');
      return;
    }
    try {
      const resp = await fetch(api('/api/card/activate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardCode: cardCode.trim(), email: redeemEmail }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setRedeemMsg(data.error || '激活失败');
        return;
      }
      setRedeemOk(true);
      setRedeemMsg('激活成功，您的同步令牌如下：');
      setRedeemToken(data.syncToken || '');
    } catch {
      setRedeemMsg('网络错误，请稍后重试');
    }
  };

  const handlePurchase = async (plan: 'annual' | 'lifetime') => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg('请输入有效的邮箱地址');
      setStep('error');
      return;
    }
    setSelectedPlan(plan);
    setSelectedPlanItem(null);
    setStep('paying');
    setErrorMsg('');

    try {
      const resp = await fetch(api('/api/payment/create-order'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planCode: plan === 'annual' ? 'catalog_yearly' : 'catalog_lifetime', email }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setErrorMsg(data.error || '创建订单失败');
        setStep('error');
        return;
      }
      setQrDataUrl(data.qrDataUrl);
      setOutTradeNo(data.outTradeNo);

      pollRef.current = setInterval(async () => {
        try {
          const sr = await fetch(api(`/api/payment/order-status/${data.outTradeNo}`));
          const sd = await sr.json();
          if (sd.status === 'paid') {
            if (pollRef.current) clearInterval(pollRef.current);
            setSyncToken(sd.syncToken);
            setStep('success');
          }
        } catch { }
      }, 3000);
    } catch (err: any) {
      setErrorMsg(err?.message || '网络错误');
      setStep('error');
    }
  };

  const handlePurchasePlan = async (plan: PlanItem) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErrorMsg('请输入有效的邮箱地址');
      setStep('error');
      return;
    }
    setSelectedPlanItem(plan);
    setStep('paying');
    setErrorMsg('');

    try {
      const resp = await fetch(api('/api/payment/create-order'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planCode: plan.code, email }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setErrorMsg(data.error || '创建订单失败');
        setStep('error');
        return;
      }
      setQrDataUrl(data.qrDataUrl);
      setOutTradeNo(data.outTradeNo);

      pollRef.current = setInterval(async () => {
        try {
          const sr = await fetch(api(`/api/payment/order-status/${data.outTradeNo}`));
          const sd = await sr.json();
          if (sd.status === 'paid') {
            if (pollRef.current) clearInterval(pollRef.current);
            setSyncToken(sd.syncToken);
            setStep('success');
          }
        } catch { }
      }, 3000);
    } catch (err: any) {
      setErrorMsg(err?.message || '网络错误');
      setStep('error');
    }
  };

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setStep('pricing');
    setQrDataUrl('');
    setOutTradeNo('');
    setSyncToken('');
    setErrorMsg('');
    setSelectedPlanItem(null);
  };

  return (
    <div className="max-w-5xl mx-auto px-6 pt-14 pb-24">
      {step === 'pricing' && (
        <>
          <div className="text-center mb-12">
            <div className="hud-label mb-3 flex items-center justify-center gap-3">
              <span className="status-dot on" /> Sync Access · Unlock
            </div>
            <h1 className="font-display font-bold text-[clamp(2.2rem,4.5vw,3.6rem)] tracking-tight leading-tight mb-4">
              {title || '定价方案'}<span className="gradient-text">_</span>
            </h1>
            <p className="text-[var(--ink-dim)]">{subtitle || '为 CXAI-FreeRouter 提供实时目录数据同步'}</p>
          </div>

          {!payEnabled && (
            <div className="glass-card mb-8 border-[rgba(255,200,90,0.3)] !bg-[rgba(255,200,90,0.06)] px-6 py-4 text-center text-sm text-[#ffd37a]">
              微信扫码支付暂未开通，可联系管理员获取兑换码，在下方「兑换码激活」处自助开通
            </div>
          )}

          <div className="mb-10 max-w-md mx-auto">
            <label className="hud-label mb-2 block">邮箱地址 · 用于接收同步令牌</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="sci-input w-full px-4 py-3.5"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
            <div className="relative glass-card overflow-hidden p-8 md:-translate-y-2 border-[var(--line-strong)] shadow-[var(--shadow-glow)]">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--accent)] to-transparent" />
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[rgba(94,242,224,0.1)] blur-2xl" />
              <div className="relative">
                <div className="mb-5 flex items-center justify-between">
                  <h2 className="font-display text-xl font-semibold">年度订阅</h2>
                  <span className="neon-btn px-3.5 py-1 !text-xs !font-medium">推荐</span>
                </div>
                <div className="font-display font-bold text-[2.75rem] tracking-tight mb-1">
                  <span className="gradient-text">¥69</span>
                  <span className="ml-1 text-base font-medium text-[var(--ink-dim)]">/年</span>
                </div>
                <p className="text-sm text-[var(--ink-dim)] mb-7">实时目录同步 · 智能体发现</p>
                <ul className="mb-9 space-y-3 text-sm">
                  {PLAN_FEATURES.annual.map((f, i) => (
                    <li key={i} className="flex items-center gap-2.5">
                      <Check size={15} className="shrink-0 text-[var(--accent)]" strokeWidth={2.4} />
                      <span className="text-[var(--ink)]/90">{f}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => handlePurchase('annual')} disabled={!payEnabled || !email} className="neon-btn w-full py-3.5 text-base">立即订阅</button>
              </div>
            </div>

            <div className="glass-card p-8">
              <h2 className="font-display text-xl font-semibold mb-5">终身版</h2>
              <div className="font-display font-bold text-[2.75rem] tracking-tight mb-1">
                <span className="text-[var(--ink)]">¥199</span>
                <span className="ml-1 text-base font-medium text-[var(--ink-dim)]">一次性</span>
              </div>
              <p className="text-sm text-[var(--ink-dim)] mb-7">永久访问 · 无需续费</p>
              <ul className="mb-9 space-y-3 text-sm">
                {PLAN_FEATURES.lifetime.map((f, i) => (
                  <li key={i} className="flex items-center gap-2.5">
                    <Check size={15} className="shrink-0 text-[var(--accent-2)]" strokeWidth={2.4} />
                    <span className="text-[var(--ink)]/90">{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => handlePurchase('lifetime')} disabled={!payEnabled || !email} className="ghost-btn w-full py-3.5 text-base">购买终身版</button>
            </div>
          </div>

          {plans.filter(p => p.kind === 'router').length > 0 && (
            <div className="mt-14">
              <div className="flex items-center gap-2 mb-5 hud-label"><Cpu size={14} /> 企业版软件授权</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
                {plans.filter(p => p.kind === 'router').map(plan => (
                  <div key={plan.code} className="glass-card p-7">
                    <h2 className="font-display text-lg font-semibold mb-4">{plan.name}</h2>
                    <div className="font-display font-bold text-[2.4rem] tracking-tight mb-1">
                      <span className="text-[var(--ink)]">{plan.displayPrice}</span>
                      {plan.durationDays === 0
                        ? <span className="ml-1 text-sm font-medium text-[var(--ink-dim)]">一次性</span>
                        : <span className="ml-1 text-sm font-medium text-[var(--ink-dim)]">/{plan.durationDays >= 365 ? '年' : '月'}</span>}
                    </div>
                    <p className="text-sm text-[var(--ink-dim)] mb-6">{plan.description}</p>
                    <ul className="mb-7 space-y-2.5 text-sm">
                      {plan.features.map((f, i) => (
                        <li key={i} className="flex items-center gap-2.5">
                          <Check size={14} className="shrink-0 text-[var(--accent)]" strokeWidth={2.4} />
                          <span className="text-[var(--ink)]/90">{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button onClick={() => handlePurchasePlan(plan)} disabled={!payEnabled || !email} className="ghost-btn w-full py-3 text-sm">立即购买</button>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-[var(--ink-dim)] leading-relaxed">
                企业版购买后自动发放授权卡密（CXAR- 开头）。在 CXAI-FreeRouter 客户端「软件授权」处输入卡密与机器码完成激活（一卡一机）。
              </p>
            </div>
          )}

          <div className="mt-14 text-center hud-label !text-[0.65rem] leading-relaxed">
            <p>FREE TIER · 免费版可浏览目录，但不包含实时同步 API</p>
            <p className="mt-1.5">CXAI-FREEROUTER 通过同步 API 自动拉取最新模型数据</p>
          </div>

          <div className="mt-14 max-w-md mx-auto glass-card border-[var(--line)] px-6 py-6">
            <div className="hud-label mb-4 text-center">客服微信 · Customer Service</div>
            {csQr ? (
              <div className="flex flex-col items-center gap-4">
                <img src={csQr} alt="客服微信二维码" className="h-44 w-44 rounded-lg border border-[var(--line)] bg-white p-2 object-contain" />
                <p className="text-sm text-[var(--ink-dim)] text-center">{csText || '扫码添加客服微信，获取兑换码 / 咨询开通'}</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="flex h-44 w-44 items-center justify-center rounded-lg border-2 border-dashed border-[var(--line)] text-center">
                  <span className="px-4 text-xs text-[var(--ink-dim)]">客服二维码待配置<br />在后台「页面设计 → 定价页 → 定价区块属性」填写图片地址</span>
                </div>
                <p className="text-sm text-[var(--ink-dim)] text-center">{csText || '扫码添加客服微信，获取兑换码 / 咨询开通'}</p>
              </div>
            )}
          </div>

          <div className="mt-14 max-w-md mx-auto glass-card border-[var(--line)] px-6 py-5">
            <button onClick={() => setRedeemOpen(!redeemOpen)} className="w-full flex items-center justify-between text-left">
              <span className="hud-label">已有兑换码？立即激活</span>
              <span className={`text-[var(--accent)] transition-transform ${redeemOpen ? 'rotate-180' : ''}`}>▾</span>
            </button>
            {redeemOpen && (
              <div className="mt-5 space-y-4">
                <div className="rounded-lg border border-[rgba(94,242,224,0.2)] bg-[rgba(7,13,28,0.6)] px-4 py-3.5 text-sm text-[var(--ink)]/90 leading-relaxed space-y-2">
                  <p className="font-medium text-[var(--accent)]">模型更新卡密 · 激活方法</p>
                  <p className="text-xs text-[var(--ink-dim)]">用于激活「模型更新·年会员 / 永久会员」服务</p>
                  <p>1. 打开客户端 → 「网站模型目录同步」</p>
                  <p>2. 输入兑换码，点击激活</p>
                  <p>3. 激活后自动获取同步令牌，开启实时数据同步</p>
                </div>
                <div className="rounded-lg border border-[rgba(94,242,224,0.2)] bg-[rgba(7,13,28,0.6)] px-4 py-3.5 text-sm text-[var(--ink)]/90 leading-relaxed space-y-2">
                  <p className="font-medium text-[var(--accent)]">软件授权卡密 · 激活方法</p>
                  <p className="text-xs text-[var(--ink-dim)]">用于激活「企业版软件授权 · 月付 / 年付 / 终身」</p>
                  <p>1. 打开客户端 → 「软件授权」</p>
                  <p>2. 输入兑换码和机器码，点击激活</p>
                  <p>3. 激活后自动绑定设备（一卡一机），解锁全部路由策略</p>
                </div>
                <p className="text-xs text-[var(--ink-dim)] leading-relaxed">所有兑换码均需在客户端软件中激活，不支持在此网站直接激活。</p>
              </div>
            )}
          </div>
        </>
      )}

      {step === 'paying' && (
        <div className="text-center pt-6">
          <div className="hud-label mb-3 flex items-center justify-center gap-3">
            <span className="status-dot on" /> WeChat Pay · Pending
          </div>
          <h2 className="font-display text-2xl font-semibold mb-2">微信扫码支付</h2>
          <p className="text-[var(--ink-dim)] mb-10">{selectedPlanItem ? selectedPlanItem.name : (selectedPlan === 'annual' ? '年度订阅 ¥69' : '终身版 ¥199')}</p>
          {qrDataUrl && (
            <div className="glass-card inline-block p-6 mb-8 !bg-white">
              <img src={qrDataUrl} alt="微信支付二维码" className="h-64 w-64 rounded-lg" />
            </div>
          )}
          <p className="text-sm text-[var(--ink-dim)]">请使用微信扫描二维码完成支付</p>
          <p className="hud-label mt-3 !text-[0.6rem]">支付成功后将自动显示同步令牌</p>
          <button onClick={reset} className="ghost-btn mt-8 px-6 py-2.5 text-sm">取消</button>
        </div>
      )}

      {step === 'success' && (
        <div className="text-center pt-6 max-w-lg mx-auto">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border border-[var(--line-strong)] bg-[rgba(94,242,224,0.1)] text-[var(--accent)] shadow-[var(--shadow-glow)]">
            <CheckCircle2 size={30} strokeWidth={2} />
          </div>
          <h2 className="font-display text-2xl font-semibold text-[var(--accent)] mb-2">支付成功</h2>
          <p className="text-[var(--ink-dim)] mb-9">{selectedPlanItem ? `${selectedPlanItem.name}已开通` : (selectedPlan === 'annual' ? '年度订阅已开通' : '终身版已开通')}</p>
          <div className="glass-card border-[rgba(94,242,224,0.3)] p-6 mb-7 text-left">
            <p className="hud-label mb-3">您的同步令牌 · Sync Token</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-lg border border-[var(--line)] bg-[rgba(7,13,28,0.6)] px-3 py-2.5 font-mono text-sm text-[var(--accent)]">{syncToken}</code>
              <button onClick={() => navigator.clipboard.writeText(syncToken)} className="ghost-btn flex shrink-0 items-center gap-1.5 px-3.5 py-2.5 !text-xs"><Copy size={12} /> 复制</button>
            </div>
            <p className="mt-4 text-xs text-[var(--ink-dim)] leading-relaxed">将同步令牌填入 CXAI-FreeRouter 客户端配置即可启用实时数据同步。令牌与邮箱绑定，请妥善保管。</p>
          </div>
          <button onClick={reset} className="ghost-btn px-6 py-2.5 text-sm"><ArrowLeft size={14} className="mr-1.5 inline" /> 返回</button>
        </div>
      )}

      {step === 'error' && (
        <div className="text-center pt-6 max-w-lg mx-auto">
          <h2 className="font-display text-2xl font-semibold mb-3">无法完成操作</h2>
          <p className="text-[#ff5d7a] mb-9">{errorMsg || '请重试'}</p>
          <button onClick={reset} className="ghost-btn px-6 py-2.5 text-sm"><ArrowLeft size={14} className="mr-1.5 inline" /> 返回</button>
        </div>
      )}
    </div>
  );
}
